// GiftReceiptModal.jsx
// Modal wrapper for GiftReceiptTemplate.
// Actions: Print/Save PDF, Email Receipt, Mark Acknowledged.
// Loads gift + member + fund data on open.
// Path: src/components/features/GiftReceiptModal.jsx

import React, { useState, useEffect } from "react";
import {
  X,
  Printer,
  Mail,
  CheckCircle,
  AlertCircle,
  Loader,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { giftsService } from "../../services/giftsService";
import { GiftReceiptTemplate } from "./GiftReceiptTemplate";
import { useApp } from "../../context/AppContext";

// ── Props ─────────────────────────────────────────────────────
// gift         — full gift object (from giftsService)
// member       — full member object
// onClose      — called when modal is dismissed
// onAcknowledged — called after gift is marked acknowledged (optional)

export const GiftReceiptModal = function (props) {
  var gift = props.gift;
  var member = props.member;
  var onClose = props.onClose;
  var onAcknowledged = props.onAcknowledged;

  var { palette, chapterSettings, funds } = useApp();

  const [acknowledging, setAcknowledging] = useState(false);
  const [acknowledged, setAcknowledged] = useState(
    gift ? !!gift.acknowledgedAt : false
  );
  const [ackError, setAckError] = useState(null);
  const [generatingReceipt, setGeneratingReceipt] = useState(false);

  // Find fund name from funds list in AppContext
  var fundName = null;
  if (gift && gift.fundId && funds) {
    var found = funds.find(function (f) {
      return f.id === gift.fundId;
    });
    if (found) fundName = found.name;
  }

  // Close on Escape key
  useEffect(
    function () {
      var handler = function (e) {
        if (e.key === "Escape") onClose();
      };
      document.addEventListener("keydown", handler);
      return function () {
        document.removeEventListener("keydown", handler);
      };
    },
    [onClose]
  );

  // ── Print handler ─────────────────────────────────────────
  var handlePrint = function () {
    window.print();
  };

  // ── Email handler ─────────────────────────────────────────
  var handleEmail = function () {
    var email = member.email || "";
    var orgName =
      (chapterSettings && chapterSettings.legalName) || "our organization";
    var receiptNum =
      gift && gift.receiptNumber ? gift.receiptNumber : "your receipt";
    var donorFirst = member.firstName || "Friend";

    var subject = encodeURIComponent(
      "Gift Acknowledgment from " + orgName + " -- " + receiptNum
    );

    var body = encodeURIComponent(
      "Dear " +
        donorFirst +
        ",\n\n" +
        "Please find attached your gift receipt " +
        receiptNum +
        " from " +
        orgName +
        ".\n\n" +
        "Thank you for your generous support. Please retain this receipt for your tax records.\n\n" +
        "With gratitude,\n" +
        ((chapterSettings && chapterSettings.signatureName) || orgName)
    );

    var mailto = "mailto:" + email + "?subject=" + subject + "&body=" + body;
    window.location.href = mailto;
  };

  // ── Mark Acknowledged handler ─────────────────────────────
  var handleMarkAcknowledged = async function () {
    if (acknowledged || acknowledging) return;
    setAcknowledging(true);
    setAckError(null);
    try {
      await giftsService.markAcknowledged(gift.id);
      setAcknowledged(true);
      if (onAcknowledged) onAcknowledged(gift.id);
    } catch (err) {
      setAckError("Could not mark acknowledged. Please try again.");
    } finally {
      setAcknowledging(false);
    }
  };

  if (!gift || !member) return null;

  // ── Styles ────────────────────────────────────────────────
  var overlay = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    zIndex: 1000,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "32px 16px",
    overflowY: "auto",
  };

  var modalBox = {
    background: palette.cardBg,
    borderRadius: 10,
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    width: "100%",
    maxWidth: 760,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  };

  var toolbar = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "14px 20px",
    borderBottom: "1px solid " + palette.cardBorder,
    background: palette.toolbarBg || palette.cardBg,
    borderRadius: "10px 10px 0 0",
    flexShrink: 0,
  };

  var receiptBody = {
    overflowY: "auto",
    padding: "24px",
    background: "#f0f0f0",
  };

  var actionBar = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "14px 20px",
    borderTop: "1px solid " + palette.cardBorder,
    background: palette.toolbarBg || palette.cardBg,
    borderRadius: "0 0 10px 10px",
    flexShrink: 0,
  };

  var btnBase = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    borderRadius: 6,
    fontSize: FONT_SIZES.sm,
    fontWeight: 600,
    fontFamily: FONT_FAMILY,
    cursor: "pointer",
    border: "none",
    transition: "opacity 0.15s",
  };

  var btnPrimary = Object.assign({}, btnBase, {
    background: palette.accent1,
    color: "#ffffff",
  });

  var btnSecondary = Object.assign({}, btnBase, {
    background: "transparent",
    color: palette.text1,
    border: "1px solid " + palette.cardBorder,
  });

  var btnSuccess = Object.assign({}, btnBase, {
    background: acknowledged ? "#d1fae5" : "#10b981",
    color: acknowledged ? "#065f46" : "#ffffff",
    cursor: acknowledged ? "default" : "pointer",
    opacity: acknowledging ? 0.7 : 1,
  });

  var giftTypeLabel =
    {
      cash: "Cash / Check / Credit Card",
      inkind: "In-Kind Gift",
      stock: "Stock / Securities",
      daf: "Donor Advised Fund",
    }[gift.giftType] || gift.giftType;

  return (
    <div
      style={overlay}
      onClick={function (e) {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={modalBox}>
        {/* ── Toolbar ──────────────────────────────────────── */}
        <div style={toolbar} className="gift-receipt-no-print">
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: FONT_SIZES.sm,
                fontWeight: 700,
                color: palette.text1,
                fontFamily: FONT_FAMILY,
              }}
            >
              {gift.giftType === "daf" ? "Gift Acknowledgment" : "Gift Receipt"}
            </div>
            <div
              style={{
                fontSize: FONT_SIZES.xs,
                color: palette.text3,
                marginTop: 1,
              }}
            >
              {giftTypeLabel +
                " -- " +
                (member.firstName || "") +
                " " +
                (member.lastName || "")}
              {gift.receiptNumber ? " -- " + gift.receiptNumber : ""}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 4,
              color: palette.text3,
              display: "flex",
              alignItems: "center",
            }}
          >
            <X size={ICON_SIZES.md} />
          </button>
        </div>

        {/* ── Receipt Preview ───────────────────────────────── */}
        <div style={receiptBody}>
          <GiftReceiptTemplate
            gift={gift}
            member={member}
            chapterSettings={chapterSettings}
            fundName={fundName}
          />
        </div>

        {/* ── Action Bar ────────────────────────────────────── */}
        <div style={actionBar} className="gift-receipt-no-print">
          {/* Print / Save PDF */}
          <button style={btnPrimary} onClick={handlePrint}>
            <Printer size={ICON_SIZES.sm} />
            Print / Save PDF
          </button>

          {/* Email Receipt */}
          <button style={btnSecondary} onClick={handleEmail}>
            <Mail size={ICON_SIZES.sm} />
            Email Receipt
          </button>

          <div style={{ flex: 1 }} />

          {/* Error message */}
          {ackError && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: FONT_SIZES.xs,
                color: "#991b1b",
              }}
            >
              <AlertCircle size={ICON_SIZES.sm} />
              {ackError}
            </div>
          )}

          {/* Mark Acknowledged */}
          <button
            style={btnSuccess}
            onClick={handleMarkAcknowledged}
            disabled={acknowledged || acknowledging}
          >
            {acknowledging ? (
              <Loader
                size={ICON_SIZES.sm}
                style={{ animation: "spin 1s linear infinite" }}
              />
            ) : (
              <CheckCircle size={ICON_SIZES.sm} />
            )}
            {acknowledged ? "Acknowledged" : "Mark Acknowledged"}
          </button>
        </div>
      </div>

      {/* Spin animation for loader */}
      <style>{"@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }"}</style>
    </div>
  );
};
